import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, formatWorkMinutes } from 'helpers/helpers'

/**
 * ISHLAB CHIQARISH KENGASHI — IXCHAM KO'RINISH (sotuvchi va admin uchun)
 * 2026-08-05
 *
 * NEGA KERAK: sotuvchi ko'radigan kartochka (`OrderCard`) 273px balandlikda edi —
 * 1080p ekranda ustunga atigi 2 ta zakaz sig'ardi. Prodда bitta tenantda 26 ta
 * karta bo'lgan, ya'ni sotuvchi 10 marta pastga surishi kerak edi.
 * Sex va omborchi uchun 2026-07-28 da ixcham kartochka (`KbCard`) yozilgan,
 * sotuvchi esa chetda qolib ketgan.
 *
 * ⚠️ BU KO'RINISH FAQAT SOTUVCHI/EGA/DIREKTOR UCHUN.
 *    Usta (ROLE_WORKSHOP), omborchi (ROLE_WAREHOUSE) va ishlab chiqarish rahbari
 *    (ROLE_PROD_MANAGER) ko'rinishlariga UMUMAN tegilmagan — ular o'z ekranida qoladi.
 *
 * TUZILISHI (user bilan maket orqali kelishilgan):
 *   · 3 ta bo'lim ustuni: Profnastil · Aksessuar · Tunukafon
 *   · Ombor ALOHIDA USTUN EMAS — tayyor chek o'z bo'limida qoladi va
 *     qatorida «Омборда» deb yoziladi, yonida necha kun turgani.
 *   · Qator: chek raqami · bosqich chizig'i · vada. Boshqa hech narsa.
 *   · Vada kun nomi bilan: Бугун / Эртага / Индинга, undan narisi sana.
 *   · Chop etish tugmalari YO'Q — sotuvchi kengashdan chek chiqarmaydi.
 *   · Pastda «Чиқиб кетганлар» — faqat bugun, kecha va ilgari kuni.
 */

const DAY = 86400000

const DEPT_KEY = { 4: 'prof', 3: 'aks', 5: 'tun' }

function startOfDay(value) {
	const d = new Date(value)
	d.setHours(0, 0, 0, 0)
	return d
}

/** Chek raqami — qo'shimcha chek bo'lsa «1234-A» ko'rinishida */
function chequeNo(order) {
	return order.parent_id
		? `${order.parent_id}-${order.cheque_suffix}`
		: order.cheque_suffix === 'A' ? `${order.id}-A` : order.id
}

/**
 * VADA MATNI.
 * Yaqin uch kun SO'Z bilan (user talabi: «bugun ertaga indinga degandik,
 * indindan ertasini sanaga o'girsin»), undan narisi sana bilan.
 * Tayyor (omborda turgan) chek uchun esa vada emas — NECHA KUN kutayotgani.
 */
function vadaOf(order) {
	if (order.warehouse_ready) {
		if (!order.ready_at) return { text: '—', cls: 'is-none' }
		const days = Math.floor((Date.now() - new Date(order.ready_at)) / DAY)
		// 7 kundan oshgani — muammo, qizil. 3–7 sariq, yangisi yashil.
		const cls = days >= 7 ? 'is-late' : days >= 3 ? 'is-soon' : 'is-ok'
		return { text: days <= 0 ? 'Бугун' : `${days} кун`, cls }
	}
	if (!order.delivery_date) return { text: 'вада йўқ', cls: 'is-none' }

	const diff = Math.round((startOfDay(order.delivery_date) - startOfDay(new Date())) / DAY)
	if (diff < 0) return { text: `${Math.abs(diff)} кун кеч`, cls: 'is-late' }
	if (diff === 0) {
		// Bugungi vada: soati o'tgan bo'lsa ham «Бугун», lekin qizil
		const past = new Date(order.delivery_date).getTime() < Date.now()
		return { text: 'Бугун', cls: past ? 'is-late' : 'is-soon' }
	}
	if (diff === 1) return { text: 'Эртага', cls: 'is-soon' }
	if (diff === 2) return { text: 'Индинга', cls: '' }
	return { text: formatDate(order.delivery_date, 'dd.MM'), cls: '' }
}

/**
 * BOSQICH — uch nuqtali chiziq.
 * 0 = янги (hali boshlanmagan) · 1 = жараёнда · 2 = тайёр/омборда
 *
 * ⚠️ `warehouse_ready` backendда `bool_and(...)` bilan HAMMA bo'lim bo'yicha
 *    hisoblanadi (ChequeController::board()), ya'ni 3 bo'limli chek uchalasi
 *    tayyor bo'lgandagina «Омборда» bo'ladi.
 */
function stepOf(order, deptType) {
	if (order.is_paused) return { i: 1, label: 'Тўхтатилган', tone: 'a' }
	if (order.warehouse_ready) return { i: 2, label: 'Омборда', tone: 'g' }

	const d = (order.departments || []).find(x => Number(x.product_type) === Number(deptType))
	if (!d) return { i: 0, label: 'Янги', tone: '' }
	if (d.dept_ready) return { i: 2, label: d.status_name || 'Тайёр', tone: 'g' }
	if (d.is_initial) return { i: 0, label: d.status_name || 'Янги', tone: '' }
	return { i: 1, label: d.status_name || 'Жараёнда', tone: '' }
}

/** Chap qirradagi rang chizig'i — vada holati */
function toneOf(order) {
	if (order.warehouse_ready) {
		const days = order.ready_at ? Math.floor((Date.now() - new Date(order.ready_at)) / DAY) : 0
		return days >= 7 ? 'late' : days >= 3 ? 'soon' : 'ok'
	}
	if (order.is_paused) return 'soon'
	if (!order.delivery_date) return 'none'
	const diff = Math.round((startOfDay(order.delivery_date) - startOfDay(new Date())) / DAY)
	if (diff < 0) return 'late'
	if (diff <= 1) return 'soon'
	return 'flow'
}

/** Tartib: eng shoshilinchi tepada */
function rank(order) {
	if (order.warehouse_ready) {
		const days = order.ready_at ? Math.floor((Date.now() - new Date(order.ready_at)) / DAY) : 0
		return days >= 7 ? 0 : 40          // uzoq turgani tepaga, yangisi pastroq
	}
	if (!order.delivery_date) return 90     // vadasiz — eng pastda
	const diff = Math.round((startOfDay(order.delivery_date) - startOfDay(new Date())) / DAY)
	if (diff < 0) return 10
	if (diff === 0) return 20
	if (diff === 1) return 30
	return 50 + Math.min(diff, 30)
}

/**
 * ⚠️ «ESKI/TASHLANDIQ» TUSHUNCHASI BU YERDA YO'Q — ATAYLAB.
 *
 * Kartochka ko'rinishida (`Board.js: ABANDONED_DAYS = 7`) vadasi 7 kundan oshgan
 * zakazlar yig'ib qo'yiladi. Ixcham ko'rinishda BUNDAY QILINMAYDI (user qarori,
 * 2026-08-05): «bu bo'limda chiqib ketmagan ish qolmasligi kerak … eski degan
 * narsa qolmasligi kerak, hammasini chiqarish kerak, chiqib ketmaganligini bilib
 * turishimiz kerak». Zakaz mijoz sababli cho'zilishi mumkin — bu uni yo'q qilmaydi.
 * Shuning uchun har bir chek ustunda oxirigacha ko'rinib turadi.
 */
function sortRows(list) {
	return [...list].sort((a, b) => {
		const r = rank(a) - rank(b)
		if (r) return r
		const da = a.delivery_date ? new Date(a.delivery_date).getTime() : Infinity
		const db = b.delivery_date ? new Date(b.delivery_date).getTime() : Infinity
		if (da !== db) return da - db
		return Number(b.id) - Number(a.id)
	})
}

/* ═══════════════════════ QATOR ═══════════════════════ */
/* Omborda turib qolish sabablari — backend OrderControlController::STALL_REASONS
   bilan AYNAN bir xil kodlar (2026-08-26, egasining talabi) */
const STALL_LABELS = {
	mijoz_oladi:    'Мижоз ўзи олиб кетади',
	mashina_keladi: 'Ўз машинаси келади',
	bolimda_qolgan: 'Бўлимда қолиб кетган',
	mahsulot_yoq:   'Маҳсулот йўқ',
	boshqa:         'Бошқа',
}

function Row({ order, deptType, open, onToggle }) {
	const st = stepOf(order, deptType)
	const vd = vadaOf(order)
	const tone = toneOf(order)

	return (
		<button type="button" className={`bc-r ${tone}`} onClick={() => onToggle(order.id)}
			aria-expanded={open}>
			<span className="bc-no">#{chequeNo(order)}{order.stall_reason &&
				<i title={STALL_LABELS[order.stall_reason] || order.stall_reason}
					style={{ color: '#f59e0b', fontStyle: 'normal' }}> ●</i>}</span>
			{/* SVETOFOR (2026-09-01, egasining talabi): yonayotgan nuqta rangi
			    BOSQICHGA qarab — 1) Янги = qizil, 2) Жараёнда = sariq, 3) Тайёр = yashil.
			    Shu sabab `s0/s1/s2` klassi qo'shiladi (ilgari rang «tone» dan olinardi
			    va yangi/jarayon ikkisi ham ko'k edi). */}
			<span className={'bc-step s' + st.i + (st.tone ? ' t-' + st.tone : '')}>
				<span className="bc-dots">
					<i className={'bc-dot' + (st.i > 0 ? ' done' : st.i === 0 ? ' now' : '')} />
					<i className={'bc-bar' + (st.i > 1 ? ' done' : '')} />
					<i className={'bc-dot' + (st.i > 1 ? ' done' : st.i === 1 ? ' now' : '')} />
					<i className="bc-bar" />
					<i className={'bc-dot' + (st.i === 2 ? ' now' : '')} />
				</span>
				<span className={'bc-lbl' + (st.tone ? ' ' + st.tone : '')}>{st.label}</span>
			</span>
			<span className={'bc-vd ' + vd.cls}>{vd.text}</span>
		</button>
	)
}

/* ═══════════════ QATOR OSTIDAGI TAFSILOT ═══════════════
   ⚠️ Bu yerda FAQAT o'sha bo'lim qatori chiqadi — Profnastil ustunida
      aksessuar/tunukafon ko'rsatish user talabiga zid (u yerda ular keraksiz).
      «Чиқиб кетиши керак» tushunchasi endi yo'q, shuning uchun hamma bo'limni
      bir joyda ko'rsatadigan holat ham qolmadi.
   ⚠️ Chop etish tugmalari ATAYLAB yo'q — sotuvchi/admin kengashdan chek
      chiqarmaydi, buning uchun Kassa → Cheklar bor. */
function Detail({ order, deptType, onTimeline, onOpen, warehouse = false, onShip, onPrint, onStall, onSupplier }) {
	const d = (order.departments || []).find(x => Number(x.product_type) === Number(deptType))
	const late = order.delivery_date && new Date(order.delivery_date).getTime() < Date.now()

	const qty = d
		? (Number(d.total_metr) > 0 ? `${Number(d.total_metr).toFixed(1)} m`
			: Number(d.total_kv_metr) > 0 ? `${Number(d.total_kv_metr).toFixed(1)} kv.m`
				: Number(d.total_qty) > 0 ? `${Number(d.total_qty)} дона` : '—')
		: '—'

	return (
		<div className="bc-det">
			<div className="bc-det-g">
				<div><span>Мижоз</span><span>{order.client_name || '—'}</span></div>
				<div><span>Вада</span><span>{order.delivery_date
					? formatDate(order.delivery_date, 'dd.MM.yyyy · HH:mm') : 'белгиланмаган'}</span></div>
				<div><span>Сотувчи</span><span>{order.cashier_name || '—'}</span></div>
				{order.region_name &&
					<div><span>Ҳудуд</span><span>{order.region_name}</span></div>}
				{late &&
					<div><span>Кечикиш сабаби</span>
						<span className={order.late_reason ? '' : 'bc-warn'}>
							{order.late_reason || 'ёзилмаган'}</span></div>}
				{order.is_paused &&
					<div><span>Тўхтатилган</span><span>{order.pause_reason || '—'}</span></div>}
				{warehouse && order.client_phone &&
					<div><span>Телефон</span><span>{order.client_phone}</span></div>}
				{/* Omborda turib qolish sababi — yozilgan bo'lsa HAMMAGA ko'rinadi
				    (ega/sotuvchi «nega turibdi» deb so'rab yurmasin) */}
				{order.stall_reason &&
					<div><span>Сабаб</span>
						<span style={{ color: '#f59e0b' }}>
							{STALL_LABELS[order.stall_reason] || order.stall_reason}
							{order.stall_note ? ` — ${order.stall_note}` : ''}
							{order.stall_by ? ` (${order.stall_by})` : ''}
						</span></div>}
				{(order.driver_name || order.driver_car) &&
					<div><span>Ҳайдовчи</span><span>{[order.driver_name, order.driver_car].filter(Boolean).join(' · ')}</span></div>}
			</div>

			{d &&
				<div className={'bc-det-dept' + (d.dept_ready ? ' ok' : ' go')}>
					<b>{d.type_name || '—'}</b>
					<span className="q">{qty}</span>
					<span className="s">{d.dept_ready ? 'ТАЙЁР' : (d.status_name || 'ЖАРАЁНДА')}</span>
				</div>}

			<div className="bc-det-a">
				{warehouse && <>
					{/* Chiqarish sharti Board.js shipDepts bilan bir xil:
					    biror bo'lim tayyor yoki ombor belgilagan */}
					{(order.warehouse_ready || (order.departments || []).some(x => x.dept_ready)) &&
						<button type="button" className="bc-mini pri" onClick={() => onShip && onShip(order)}>
							Чиқариш
						</button>}
					<button type="button" className="bc-mini" onClick={() => onPrint && onPrint(order)}>
						Юк хати
					</button>
					<button type="button" className="bc-mini" onClick={() => onStall && onStall(order)}>
						{order.stall_reason ? 'Сабабни ўзгартириш' : 'Сабаб ёзиш'}
					</button>
					{/* Mahsulot yo'q — darhol hamkorga zakaz berish yo'li */}
					{order.stall_reason === 'mahsulot_yoq' &&
						<button type="button" className="bc-mini" onClick={() => onSupplier && onSupplier()}>
							Ҳамкорга заказ →
						</button>}
				</>}
				<button type="button" className="bc-mini pri" onClick={() => onOpen(order)}>Чекни очиш</button>
				<button type="button" className="bc-mini" onClick={() => onTimeline(order)}>Тарих</button>
			</div>
		</div>
	)
}

/* ═══════════════════════ BO'LIM USTUNI ═══════════════════════
   Hech qanday zakaz yashirilmaydi — hammasi ro'yxatda. Lekin 1920x1080 da
   bitta ustunga ~15 tagina qator sig'adi (lokalda eng band bo'lim 16 ta,
   prodda ~20 gacha), shuning uchun qolgani PASTGA suriladi.
   ⚠️ «Ko'rinmayapti» degani «yo'q» degani emas — pastda nechta qolgani
      «яна N та» tugmasida yozilib turadi, bosilsa pastga suradi.
      Sarlavhadagi son esa DOIM to'liq sonni ko'rsatadi. */
function DeptColumn({ dept, rows, openId, setOpenId, onTimeline, onOpenCheque, warehouse, onShip, onPrint, onStall, onSupplier }) {
	const bodyRef = useRef(null)
	const [more, setMore] = useState(0)

	useLayoutEffect(() => {
		const el = bodyRef.current
		if (!el) return
		const upd = () => {
			// Pastki chegaradan chiqib ketgan qatorlarni SANAYMIZ (piksel emas —
			// foydalanuvchiga «yana 3 ta» kerak, «yana 140px» emas).
			const cut = el.scrollTop + el.clientHeight + 4
			let n = 0
			el.querySelectorAll('.bc-r').forEach(r => { if (r.offsetTop + r.offsetHeight > cut) n++ })
			setMore(n)
		}
		upd()
		el.addEventListener('scroll', upd)
		// Ustun o'lchami o'zgarsa (oyna, to'liq ekran, tafsilot ochilishi) qayta sanaymiz
		const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(upd) : null
		if (ro) ro.observe(el)
		return () => { el.removeEventListener('scroll', upd); if (ro) ro.disconnect() }
	})

	/* USTUN JAMLANMASI — «navbatda nechta chek, qancha metr, necha soatlik ish».
	   ⚠️ Raqamlar KARTOCHKALARDAN yig'iladi (backend `departments` bloki).
	      Sanoq belgisi (bc-col-c) ustundagi HAMMA qatorni ko'rsatadi, jamlanma esa
	      faqat NAVBATDAGISINI — farqi «N тайёр» bo'lagida ko'rinib turadi.
	   ⚠️ Soat — sana tavsiyasi bilan BIR XIL model: hajm ÷ sof tezlik + rulon
	      sozlash (ChequeController::board, estimated_minutes).
	   ⚠️ Tunikofon KV.METRDA, qolganlari uzunlik metrida. */
	const sum = React.useMemo(() => {
		let metr = 0, kv = 0, minutes = 0, cheques = 0, ready = 0
		for (const o of rows) {
			const d = (o.departments || []).find(x => Number(x.product_type) === Number(dept.type))
			if (!d) continue
			/* ⚠️ TAYYOR bo'lim NAVBAT EMAS. U ustunda «Омборда» deb ko'rinib turadi,
			   lekin ish hajmiga qo'shilmaydi. Bazada o'lchandi: profnastilda jami
			   751 daqiqaning 276 tasi (37%), aksessuarda 44% allaqachon bajarilgan
			   ish edi — qo'shilsa navbat shishib, reja yolg'on chiqadi.
			   Ustunlar ko'rinishidagi StatsBar ham tayyorni sanamaydi (getDeptOrders
			   `!d.dept_ready` bilan filtrlaydi). FARQI: u tashlandiqni (vadasi 7
			   kundan oshgan) ham chiqarib tashlaydi, ixcham ko'rinishda esa bunday
			   tushuncha yo'q — bu yerda hamma tirik zakaz ko'rinadi. */
			if (d.dept_ready || o.warehouse_ready) { ready++; continue }
			cheques++
			metr += Number(d.total_metr || 0)
			kv += Number(d.total_kv_metr || 0)
			minutes += Number(d.estimated_minutes || 0)
		}
		return { metr, kv, minutes, cheques, ready }
	}, [rows, dept.type])

	/* Birliklar qatorlardagi bilan bir xil: `m` va `kv.m` (ilgari «м»/«кв» edi). */
	const volText = Number(dept.type) === 5
		? (sum.kv > 0 ? `${sum.kv.toFixed(1)} kv.m` : null)
		: (sum.metr > 0 ? `${Math.round(sum.metr)} m` : null)

	const hourText = formatWorkMinutes(sum.minutes, { hour: 'соат', minute: 'дақиқа' })

	return (
		<div className={'bc-col ' + (DEPT_KEY[dept.type] || '')}>
			<div className="bc-col-h">
				<span className="bc-col-n">{dept.label}</span>
				<span className="bc-col-c">{rows.length}</span>
			</div>
			{(sum.cheques > 0 || sum.ready > 0) &&
				<div className="bc-col-st">
					<span title="Навбатда турган чеклар (тайёрлари саналмайди)">
						<i className="uil-receipt" />{sum.cheques} чек
					</span>
					{volText && <span><i className="uil-ruler" />{volText}</span>}
					{hourText &&
						<span title="Навбатдаги ишнинг тахминий вақти">
							<i className="uil-clock" />{hourText}
						</span>}
					{sum.ready > 0 &&
						<span className="ok" title="Бу бўлимда тайёр — омборда кутмоқда">
							<i className="uil-check-circle" />{sum.ready} тайёр
						</span>}
				</div>
			}
			<div className="bc-col-w">
				<div className="bc-col-b" ref={bodyRef}>
					{rows.length === 0 ? (
						<div className="bc-empty"><span className="bc-empty-i">✓</span><p>Заказ йўқ</p></div>
					) : rows.map(order => (
						<React.Fragment key={order.id}>
							<Row order={order} deptType={dept.type}
								open={openId === order.id}
								onToggle={id => setOpenId(p => p === id ? null : id)} />
							{openId === order.id &&
								<Detail order={order} deptType={dept.type}
									onTimeline={onTimeline} onOpen={onOpenCheque}
									warehouse={warehouse} onShip={onShip} onPrint={onPrint}
									onStall={onStall} onSupplier={onSupplier} />}
						</React.Fragment>
					))}
				</div>
				{more > 0 &&
					<button type="button" className="bc-more"
						onClick={() => bodyRef.current.scrollBy({ top: bodyRef.current.clientHeight * 0.8, behavior: 'smooth' })}>
						яна {more} та ↓
					</button>}
			</div>
		</div>
	)
}

/* ═══════════════ CHIQIB KETGANLAR (pastda) ═══════════════ */
function shippedDayKey(order) {
	const at = order.shipped_at || order.cheque_closed_at
	if (!at) return null
	const diff = Math.round((startOfDay(new Date()) - startOfDay(at)) / DAY)
	if (diff < 0) return 0
	// Backend 3 kunlik oyna beradi, lekin chegarada 3 kunlik qator ham kelib
	// qolishi mumkin — uni JIMGINA TASHLAMAYMIZ, «Илгари куни» ga qo'shamiz.
	return diff >= 2 ? 2 : diff
}

const SHIPPED_TITLES = ['Бугун', 'Кеча', 'Илгари куни']

/* ═══════════════════════ ASOSIY ═══════════════════════ */
/* warehouse rejimi (2026-08-26): omborchi ham shu ixcham ko'rinishda ishlaydi,
   lekin O'Z amallari bilan — Чиқариш (ship), Юк хати (chop), Сабаб ёзиш.
   Tugma handlerlari Board.js da qoladi (modallar o'sha yerda). */
export default function BoardCompact({ orders = [], completed = [], depts = [], onTimeline, warehouse = false, onShip, onPrint, onStall, onPhotos }) {
	const navigate = useNavigate()
	const [openId, setOpenId] = useState(null)
	const [q, setQ] = useState('')

	/* BALANDLIK. `.pb` (Board.js) `height:100vh` — lekin u sahifa sarlavhasi
	   OSTIDA turadi, ya'ni ekrandan sarlavha balandligicha oshib ketadi va
	   pastdagi «Чиқиб кетганлар» kesiladi (o'lchandi: 70px).
	   `.pb` ni o'zgartirish barcha rollarga tegadi, shuning uchun faqat shu
	   ko'rinishning balandligini o'zimiz o'lchab qo'yamiz — sahifa umuman
	   surilmasin (user: kengash bitta ekranda turishi kerak). */
	const rootRef = useRef(null)
	// Balandlik to'g'ridan-to'g'ri DOM ga yoziladi (state emas) — qayta render
	// halqasi bo'lmasin va ikkinchi o'lchov bir kadrда tugasin.
	const fitRef = useRef(null)
	useLayoutEffect(() => {
		const doFit = () => {
			const el = rootRef.current
			if (!el) return
			el.style.flex = 'none'
			const top = el.getBoundingClientRect().top
			const h = Math.max(320, Math.floor(window.innerHeight - top - 2))
			el.style.height = h + 'px'
			// Layout pastida hali bo'shliq qolsa (padding/futer) — ayiramiz.
			const over = document.documentElement.scrollHeight - document.documentElement.clientHeight
			if (over > 0) el.style.height = Math.max(320, h - over) + 'px'
		}
		fitRef.current = doFit
		doFit()
		window.addEventListener('resize', doFit)
		document.addEventListener('fullscreenchange', doFit)
		return () => {
			window.removeEventListener('resize', doFit)
			document.removeEventListener('fullscreenchange', doFit)
		}
	}, [])
	// Topbar balandligi o'zgarishi mumkin (sanoq kengaysa qator ko'chadi) —
	// ma'lumot yangilanganda qayta o'lchaymiz.
	useEffect(() => { fitRef.current && fitRef.current() }, [orders.length, completed.length])

	const query = q.trim().toLowerCase()

	const match = (o) => !query
		|| String(chequeNo(o)).toLowerCase().includes(query)
		|| (o.client_name || '').toLowerCase().includes(query)

	const shown = useMemo(() => orders.filter(match), [orders, query]) // eslint-disable-line
	const shownOut = useMemo(() => completed.filter(match), [completed, query]) // eslint-disable-line

	/** Bo'lim ustuniga TUSHADIGAN cheklar — HAMMASI, hech biri yashirilmaydi.
	 *  Tayyorlari ham shu yerda qoladi («Омборда» deb). Eng kechikkani tepada. */
	const forDept = (type) => sortRows(
		shown.filter(o => (o.departments || []).some(x => Number(x.product_type) === Number(type)))
	)

	const outByDay = useMemo(() => {
		const g = [[], [], []]
		shownOut.forEach(o => {
			const k = shippedDayKey(o)
			if (k !== null) g[k].push(o)
		})
		g.forEach(list => list.sort((a, b) =>
			new Date(b.shipped_at || b.cheque_closed_at) - new Date(a.shipped_at || a.cheque_closed_at)))
		return g
	}, [shownOut])

	const openCheque = (order) => navigate(`/cashbox/edit/${order.parent_id || order.id}`)

	return (
		<div className="bc" ref={rootRef}>
			<style>{BC_CSS}</style>
			<div className="bc-search">
				<i className="uil-search" />
				<input value={q} onChange={e => setQ(e.target.value)}
					placeholder="Чек рақами ёки мижоз…" />
				{q && <button type="button" className="bc-x" onClick={() => setQ('')} title="Тозалаш">×</button>}
			</div>

			<div className="bc-cols">
				{depts.map(dept => (
					<DeptColumn key={dept.type} dept={dept} rows={forDept(dept.type)}
						openId={openId} setOpenId={setOpenId}
						onTimeline={onTimeline} onOpenCheque={openCheque}
						warehouse={warehouse} onShip={onShip} onPrint={onPrint}
						onStall={onStall} onSupplier={() => navigate('/supplier-orders')} />
				))}
			</div>

			{/* Chiqib ketganlar — faqat 3 kun, yangilari tepada */}
			<div className="bc-out">
				<div className="bc-out-h">
					<span className="t">Чиқиб кетганлар</span>
					<span className="n">{outByDay.reduce((s, l) => s + l.length, 0)}</span>
					<span className="hint">бугун · кеча · илгари куни</span>
				</div>
				<div className="bc-out-list">
					{outByDay.map((list, i) => (
						<div key={i} className="bc-out-col">
							<div className="bc-day">{SHIPPED_TITLES[i]}</div>
							{list.length === 0
								? <div className="bc-out-empty">—</div>
								: list.map(o => (
									<div key={o.id} className="bc-out-r" onClick={() => onTimeline(o)}>
										<span className="no">#{chequeNo(o)}</span>
										<span className="cl">{o.client_name || '—'}</span>
										{/* YUK RASMI (2026-09-01): egasi/direktor uchun ham —
										    o'g'irlik nazorati aynan shu yerda ishlaydi.
										    Yashil kamera = rasm bor (bosilsa ochiladi),
										    xira = rasm olinmagan. */}
										{onPhotos &&
											<span className={'bc-cam' + (Number(o.shipment_photos) > 0 ? ' has' : '')}
												title={Number(o.shipment_photos) > 0
													? `Юк расми (${o.shipment_photos} та)`
													: 'Юк расмга олинмаган'}
												onClick={(e) => { e.stopPropagation(); if (Number(o.shipment_photos) > 0) onPhotos(o) }}>
												<i className="uil-camera" />
											</span>}
										<span className="tm">{formatDate(o.shipped_at || o.cheque_closed_at, 'HH:mm')}</span>
									</div>
								))}
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

/* ═══════════════════════════ STIL ═══════════════════════════
   ⚠️ Hamma klass `bc-` bilan boshlanadi — `PB_CSS` (Board.js) bilan
      to'qnashmasligi uchun. Bitta ham umumiy nom yo'q.
   ⚠️ O'lchamlar `clamp(min, vw, max)` — 50 dyuymli televizorda ham,
      noutbukda ham o'qiladigan bo'lsin (user: «sotuvchi uzoqdan ko'radi»).
      vw o'sgani sayin shrift o'sadi, lekin `max` bilan cheklangan. */
const BC_CSS = `
/* .pb sahifa sarlavhasi ostida turadi — 100vh unga ortiqcha keladi.
   Ixcham rejimda balandlikni .bc o'zi o'lchaydi (JS), .pb esa kontentga qarab. */
.pb.pb-fit{height:auto;min-height:0;overflow:visible}
.bc{flex:1;min-height:0;display:flex;flex-direction:column;gap:clamp(8px,1vh,18px);
	padding:clamp(8px,.9vh,16px) clamp(8px,.7vw,18px) clamp(10px,1.1vh,20px);
	font-family:Arial,"Helvetica Neue",Helvetica,sans-serif;overflow:hidden;
	/* ⚠️ Bootstrap body ga line-height:1.5 beradi va u meros bo'lib tushadi —
	   o'lchandi: 25px chek raqami QATORDA 38px joy egallardi (13px bekorga).
	   1.2 Arial uchun xavfsiz (ascent+descent ≈ 1.15), kirill tagi kesilmaydi.
	   Shrift O'ZGARTIRILMAGAN — joy faqat ortiqcha bo'shliqdan olindi. */
	line-height:1.2}
.bc *{box-sizing:border-box}

/* ── Qidiruv ── */
.bc-search{position:relative;flex:none;display:flex;align-items:center;
	background:#fff;border:1px solid #dbe2ec;border-radius:10px;
	padding:0 clamp(10px,.7vw,16px);height:clamp(34px,3.4vh,50px);
	max-width:clamp(280px,26vw,520px);box-shadow:0 1px 2px rgba(15,23,42,.05)}
.bc-search i{color:#94a3b8;font-size:clamp(15px,1.05vw,22px);font-style:normal;margin-right:8px}
.bc-search input{flex:1;border:0;outline:0;background:transparent;color:#1e293b;
	font-family:inherit;font-size:clamp(13px,.95vw,20px)}
.bc-search input::placeholder{color:#a8b3c4}
.bc-x{border:0;background:transparent;color:#94a3b8;cursor:pointer;line-height:1;
	font-size:clamp(18px,1.3vw,26px);padding:0 2px}
.bc-x:hover{color:#475569}

/* ── Ustunlar ── */
.bc-cols{flex:1;min-height:0;display:flex;gap:clamp(8px,.7vw,18px)}
.bc-col{flex:1;min-width:0;display:flex;flex-direction:column;background:#fff;
	border:1px solid #e3e8f0;border-radius:12px;overflow:hidden;
	box-shadow:0 1px 3px rgba(15,23,42,.06);--ac:#2563eb}
.bc-col.prof{--ac:#2563eb}
.bc-col.aks{--ac:#7c3aed}
.bc-col.tun{--ac:#0891b2}
.bc-col-h{flex:none;display:flex;align-items:center;justify-content:space-between;
	gap:8px;padding:clamp(5px,.58vh,11px) clamp(10px,.8vw,18px);
	border-top:3px solid var(--ac);border-bottom:1px solid #eef1f6;
	background:linear-gradient(180deg,color-mix(in srgb,var(--ac) 11%,transparent),transparent)}
.bc-col-n{font-size:clamp(12px,.95vw,21px);font-weight:700;letter-spacing:.09em;
	text-transform:uppercase;color:var(--ac);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
/* Ustun jamlanmasi — sarlavha OSTIDAGI yo'lak: «12 chek · 2280 m · 9 soat 48 daqiqa».
   Dizayn ustunlar korinishidagi .pb-col-stats bilan bir xil (belgi + qiymat),
   shunda ega qaysi ko'rinishda tursa ham bir xil narsani bir xil joyda ko'radi.
   Sarlavhada emas, alohida yo'lakda: torayganda matn kesilib yo'qolmasin. */
.bc-col-st{flex:none;display:flex;align-items:center;flex-wrap:wrap;
	gap:clamp(6px,.7vw,16px);padding:clamp(3px,.42vh,7px) clamp(10px,.8vw,18px);
	background:color-mix(in srgb,var(--ac) 7%,transparent);border-bottom:1px solid #eef1f6;
	font-size:clamp(10px,.72vw,15px);font-weight:700;font-variant-numeric:tabular-nums;
	color:color-mix(in srgb,var(--ac) 76%,#475569);line-height:1.35}
.bc-col-st span{display:inline-flex;align-items:center;white-space:nowrap}
.bc-col-st i{margin-right:4px;opacity:.8;font-size:1.05em}
.bc-col-st .ok{color:#15803d;font-weight:600}
.bc-col-c{flex:none;display:inline-grid;place-items:center;min-width:clamp(24px,1.8vw,38px);
	height:clamp(22px,2.2vh,34px);padding:0 8px;background:var(--ac);color:#fff;border-radius:999px;
	font-size:clamp(12px,.9vw,19px);font-weight:700;font-variant-numeric:tabular-nums}
/* Ustun tanasi + «яна N та» tugmasi uchun o'ram.
   Tugma ABSOLYUT joylashadi — u ro'yxat balandligini o'zgartirmaydi, aks holda
   «nechta qoldi» hisobi o'z-o'zini qayta ishga tushirib halqaga tushardi. */
.bc-col-w{position:relative;flex:1;min-height:0;display:flex}
.bc-col-b{flex:1;min-height:0;overflow-y:auto;overflow-x:hidden;
	padding:clamp(5px,.58vh,11px);display:flex;flex-direction:column;gap:clamp(4px,.38vh,8px)}
.bc-col-b::-webkit-scrollbar{width:8px}
.bc-col-b::-webkit-scrollbar-thumb{background:#cfd8e5;border-radius:4px}
.bc-more{position:absolute;left:50%;bottom:clamp(4px,.5vh,10px);transform:translateX(-50%);
	border:1px solid #cfd8e5;background:rgba(255,255,255,.96);color:#475569;cursor:pointer;
	border-radius:999px;font-family:inherit;font-weight:700;white-space:nowrap;
	padding:clamp(3px,.35vh,7px) clamp(10px,.75vw,18px);font-size:clamp(11px,.8vw,18px);
	box-shadow:0 2px 8px rgba(15,23,42,.14)}
.bc-more:hover{background:#fff;color:#1e293b;border-color:#94a3b8}

/* ── Qator: chek raqami · bosqich · vada ── */
/* ⚠️ Vertikal padding ATAYLAB kichik (.45vh). Shrift O'ZGARTIRILMAGAN —
   user ikki marta «kattaroq qil, sotuvchi uzoqdan ko'radi» degan. Joy faqat
   bo'sh oraliqdan olinadi: 1920x1080 da bir ustunga 10 -> 15 qator sig'adi. */
.bc-r{width:100%;display:flex;align-items:center;gap:clamp(8px,.7vw,18px);
	padding:clamp(5px,.45vh,10px) clamp(10px,.75vw,18px);
	background:#fff;border:1px solid #e6ebf2;border-left:4px solid #cbd5e1;
	border-radius:9px;cursor:pointer;text-align:left;font-family:inherit;
	transition:background .12s,box-shadow .12s}
.bc-r:hover{background:#f7f9fc;box-shadow:0 2px 6px rgba(15,23,42,.07)}
.bc-r.late{border-left-color:#dc2626;background:#fef6f6}
.bc-r.late:hover{background:#fdeeee}
.bc-r.soon{border-left-color:#d97706;background:#fffaf2}
.bc-r.soon:hover{background:#fef4e7}
.bc-r.ok{border-left-color:#059669}
.bc-r.flow{border-left-color:#94a3b8}
.bc-r.none{border-left-color:#e2e8f0}

.bc-no{flex:none;min-width:clamp(64px,5vw,110px);color:#0f172a;font-weight:700;
	font-size:clamp(16px,1.32vw,32px);font-variant-numeric:tabular-nums;letter-spacing:-.01em}

.bc-step{flex:1;min-width:0;display:flex;align-items:center;gap:clamp(7px,.55vw,14px)}
.bc-dots{flex:none;display:inline-flex;align-items:center}
/* Nuqtalar KATTALASHTIRILDI (2026-09-01): 9-17px -> 13-24px, uzoqdan ko'rinsin */
.bc-dot{width:clamp(13px,1.02vw,24px);height:clamp(13px,1.02vw,24px);border-radius:50%;
	background:#fff;border:2px solid #d3dbe6;flex:none}
.bc-dot.done{background:#9aa8ba;border-color:#9aa8ba}
/* Yonmagan/o'tilmagan nuqtalar — kul rang; yonayotgani pastda bosqich rangini oladi */
.bc-dot.now{background:#2563eb;border-color:#2563eb;box-shadow:0 0 0 4px rgba(37,99,235,.16)}
.bc-bar{width:clamp(14px,1.35vw,34px);height:3px;background:#e2e8f0;flex:none}
.bc-bar.done{background:#9aa8ba}
/* ═══ SVETOFOR: yonayotgan nuqta rangi BOSQICHGA qarab ═══
   s0 = Янги (qizil) · s1 = Жараёнда (sariq) · s2 = Тайёр/Омборда (yashil).
   Tone qoidalari (t-g/t-a) OLIB TASHLANDI — ular bilan «yangi» ham ko'k edi. */
.bc-step.s0 .bc-dot.now{background:#dc2626;border-color:#dc2626;box-shadow:0 0 0 4px rgba(220,38,38,.20)}
.bc-step.s1 .bc-dot.now{background:#f59e0b;border-color:#f59e0b;box-shadow:0 0 0 4px rgba(245,158,11,.22)}
.bc-step.s2 .bc-dot.now{background:#16a34a;border-color:#16a34a;box-shadow:0 0 0 4px rgba(22,163,74,.20)}
.bc-lbl{min-width:0;color:#64748b;font-size:clamp(11px,.85vw,19px);font-weight:600;
	white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bc-lbl.g{color:#047857}
.bc-lbl.a{color:#b45309}

.bc-vd{flex:none;text-align:right;font-size:clamp(14px,1.12vw,27px);font-weight:700;
	color:#475569;white-space:nowrap;font-variant-numeric:tabular-nums}
.bc-vd.is-late{color:#dc2626}
.bc-vd.is-soon{color:#c2740a}
.bc-vd.is-ok{color:#047857}
.bc-vd.is-none{color:#b6c0cd;font-weight:600}

.bc-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
	gap:6px;color:#b6c0cd}
.bc-empty-i{font-size:clamp(22px,1.9vw,44px);line-height:1;color:#cfe3d8}
.bc-empty p{margin:0;font-size:clamp(12px,.9vw,19px);font-weight:600}

/* ── Tafsilot (qator ostida) ── */
.bc-det{background:#f7f9fc;border:1px solid #e6ebf2;border-radius:9px;
	padding:clamp(9px,.9vh,16px) clamp(11px,.8vw,20px);
	display:flex;flex-direction:column;gap:clamp(8px,.8vh,14px);margin-top:calc(-1 * clamp(3px,.3vh,6px))}
.bc-det-g{display:grid;grid-template-columns:1fr;gap:clamp(4px,.45vh,9px)}
.bc-det-g>div{display:flex;align-items:baseline;justify-content:space-between;gap:10px;
	font-size:clamp(11.5px,.88vw,19px)}
.bc-det-g>div>span:first-child{color:#8b97a8;font-weight:600;white-space:nowrap}
.bc-det-g>div>span:last-child{color:#1e293b;font-weight:700;text-align:right;
	overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.bc-warn{color:#dc2626 !important}
.bc-det-dept{display:flex;align-items:center;gap:10px;padding:clamp(6px,.6vh,12px) clamp(9px,.65vw,16px);
	border-radius:7px;font-size:clamp(11.5px,.88vw,19px);border:1px solid transparent}
.bc-det-dept.ok{background:#ecfdf5;border-color:#bbf0d6;color:#065f46}
.bc-det-dept.go{background:#fffbeb;border-color:#fbe4b0;color:#92400e}
.bc-det-dept b{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.bc-det-dept .q{font-weight:700;font-variant-numeric:tabular-nums}
.bc-det-dept .s{font-weight:700;letter-spacing:.06em;font-size:.86em;text-transform:uppercase}
.bc-det-a{display:flex;gap:8px}
.bc-mini{border:1px solid #d7dfea;background:#fff;color:#475569;border-radius:7px;cursor:pointer;
	padding:clamp(6px,.6vh,12px) clamp(11px,.85vw,20px);font-family:inherit;
	font-size:clamp(11.5px,.88vw,19px);font-weight:700}
.bc-mini:hover{background:#eef2f7}
.bc-mini.pri{background:#2563eb;border-color:#2563eb;color:#fff}
.bc-mini.pri:hover{background:#1d4ed8}

/* ── Chiqib ketganlar (pastki tasma) ── */
.bc-out{flex:none;background:#fff;border:1px solid #e3e8f0;border-radius:12px;
	box-shadow:0 1px 3px rgba(15,23,42,.06);display:flex;flex-direction:column;
	max-height:clamp(96px,15vh,200px);overflow:hidden}
.bc-out-h{flex:none;display:flex;align-items:center;gap:10px;
	padding:clamp(6px,.65vh,13px) clamp(11px,.8vw,20px);border-bottom:1px solid #eef1f6;background:#fafbfd}
.bc-out-h .t{font-size:clamp(11px,.85vw,19px);font-weight:700;letter-spacing:.09em;
	text-transform:uppercase;color:#64748b}
.bc-out-h .n{display:inline-grid;place-items:center;min-width:clamp(22px,1.6vw,34px);
	height:clamp(20px,2vh,30px);padding:0 7px;background:#94a3b8;color:#fff;border-radius:999px;
	font-size:clamp(11px,.82vw,18px);font-weight:700;font-variant-numeric:tabular-nums}
.bc-out-h .hint{margin-left:auto;color:#b6c0cd;font-size:clamp(10.5px,.78vw,17px);font-weight:600}
.bc-out-list{flex:1;min-height:0;display:flex;gap:clamp(8px,.7vw,18px);
	padding:clamp(7px,.7vh,14px) clamp(11px,.8vw,20px) clamp(9px,.85vh,16px)}
.bc-out-col{flex:1;min-width:0;display:flex;flex-direction:column;gap:clamp(4px,.4vh,9px);
	overflow-y:auto;overflow-x:hidden}
.bc-out-col::-webkit-scrollbar{width:6px}
.bc-out-col::-webkit-scrollbar-thumb{background:#dde4ee;border-radius:3px}
.bc-day{position:sticky;top:0;background:#fff;padding-bottom:3px;color:#8b97a8;
	font-size:clamp(10.5px,.78vw,17px);font-weight:700;letter-spacing:.08em;text-transform:uppercase}
.bc-out-r{display:flex;align-items:center;gap:9px;padding:clamp(4px,.42vh,9px) clamp(8px,.6vw,14px);
	background:#f7f9fc;border:1px solid #edf1f6;border-radius:7px;cursor:pointer}
.bc-out-r:hover{background:#eef2f7}
.bc-out-r .no{flex:none;color:#334155;font-weight:700;font-size:clamp(12px,.92vw,20px);
	font-variant-numeric:tabular-nums}
.bc-out-r .cl{flex:1;min-width:0;color:#8b97a8;font-size:clamp(11px,.84vw,18px);
	overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.bc-out-r .tm{flex:none;color:#a8b3c4;font-size:clamp(10.5px,.8vw,17px);font-weight:600;
	font-variant-numeric:tabular-nums}
/* Yuk rasmi belgisi: xira = rasm yo'q, yashil = bor (bosilsa ochiladi) */
.bc-cam{flex:none;display:inline-flex;align-items:center;color:#cbd5e1;
	font-size:clamp(13px,1vw,21px);line-height:1}
.bc-cam.has{color:#16a34a;cursor:pointer}
.bc-cam.has:hover{color:#15803d}
.bc-out-empty{color:#cbd5e1;font-size:clamp(11px,.84vw,18px);padding:2px}

/* Tor ekran — ustunlar tepama-past emas, baribir 3 ta qoladi (kengash TV uchun),
   lekin 900px dan tor bo'lsa chiqib ketganlar bir ustunga tushadi */
@media (max-width:900px){
	.bc-out-list{flex-direction:column}
	.bc-out{max-height:34vh}
}
`
